export const OPERATOR_CONTEXT_HEADER = 'X-Operator-Institution-Id';

export const OPERATOR_CONTEXT_REQUIRED = 'operator_context_required';

/** The header value that asks for every institution at once instead of one operator's tree.
 * Case-sensitive on the wire: the api recognises this exact string and nothing else. */
export const PLATFORM_OPERATOR_CONTEXT = 'platform';

/** What a tab can be working in: one operator, or the whole platform. */
export type OperatorContextSelection = number | typeof PLATFORM_OPERATOR_CONTEXT;

/** An operator root the caller holds manage scope over. Its id is always
 * present — a root with no id could not be selected. */
export type HeldRoot = Readonly<{
  id: number;
  name: string | null;
}>;

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

const rowId = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

const blankToNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

/**
 * Held roots from an operator-context refusal, or null for any other error and
 * for a refusal carrying no usable root — a picker with no options to offer
 * would strand the operator with nothing to choose and no way past it. A root
 * whose name is absent is still offered, labelled from its id.
 */
export const decodeOperatorContextRefusal = (error: unknown): HeldRoot[] | null => {
  const body = asRecord((error as { data?: unknown } | null | undefined)?.data);

  if (body?.code !== OPERATOR_CONTEXT_REQUIRED) return null;

  const roots = body.held_roots;

  if (!Array.isArray(roots)) return null;

  const decoded = roots
    .map((root) => ({ id: rowId(asRecord(root)?.id), name: blankToNull(asRecord(root)?.name) }))
    .filter((root): root is HeldRoot => root.id !== null);

  return decoded.length > 0 ? decoded : null;
};
